import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag, Tag, Info, Lock } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { useCart, computeShipping } from "../context/CartContext";
import { checkout, validateDiscount } from "../lib/api";

export const CartDrawer = () => {
  const { items, updateQty, removeItem, total, open, setOpen, shipping, minOrderQty, cartEnabled, refreshSettings } = useCart();
  const [loading, setLoading] = useState(false);
  const [discountInput, setDiscountInput] = useState("");
  const [discount, setDiscount] = useState(null); // {code, discount_amount}
  const [applyingCode, setApplyingCode] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [invoice, setInvoice] = useState({
    wants_invoice: false,
    company_name: "",
    vat_number: "",
    sdi_code: "",
    pec: "",
  });

  const minQty = Number(minOrderQty) || 12;
  const cans = items.reduce((s, i) => s + (Number(i.product.can_count) || 1) * i.quantity, 0);
  const missingCans = Math.max(0, minQty - cans);
  const belowMin = cans < minQty;

  // Rileggi lo stato del negozio ogni volta che il carrello viene aperto,
  // così l'attivazione/disattivazione dall'admin ha effetto senza ricaricare la pagina.
  useEffect(() => {
    if (open && refreshSettings) refreshSettings();
  }, [open, refreshSettings]);

  const applyDiscount = async () => {
    const code = discountInput.trim();
    if (!code) return;
    setApplyingCode(true);
    try {
      const res = await validateDiscount(code, total);
      if (res.valid) {
        setDiscount({ code: res.code, discount_amount: res.discount_amount });
        toast.success(`Codice ${res.code} applicato`);
      } else {
        setDiscount(null);
        toast.error(res.message || "Codice non valido");
      }
    } catch {
      setDiscount(null);
      toast.error("Impossibile verificare il codice");
    } finally {
      setApplyingCode(false);
    }
  };

  const removeDiscount = () => {
    setDiscount(null);
    setDiscountInput("");
  };

  const shippingCost = computeShipping(total, shipping);
  const freeOver = Number(shipping?.free_over) || 0;
  const discountAmount = discount ? Math.min(Number(discount.discount_amount) || 0, total) : 0;
  const grandTotal = Math.max(0, total - discountAmount) + shippingCost;
  const missingForFree = freeOver > 0 && total < freeOver ? freeOver - total : 0;

  const handleCheckout = async () => {
    if (items.length === 0) return;
    if (!cartEnabled) {
      toast.error("Vendite momentaneamente sospese. Riprova più tardi.");
      return;
    }
    if (belowMin) {
      toast.error(`Ordine minimo ${minQty} lattine: aggiungine ancora ${missingCans}.`);
      return;
    }
    if (!acceptedTerms) {
      toast.error("Devi accettare le condizioni di acquisto per procedere.");
      return;
    }
    if (invoice.wants_invoice) {
      if (!invoice.company_name.trim() || !invoice.vat_number.trim() || (!invoice.sdi_code.trim() && !invoice.pec.trim())) {
        toast.error("Per la fattura inserisci ragione sociale, P.IVA e Codice SDI o PEC.");
        return;
      }
    }
    setLoading(true);
    try {
      const payload = items.map((i) => ({
        product_id: i.product.id,
        quantity: i.quantity,
      }));
      const { checkout_url } = await checkout(payload, window.location.origin, invoice, discount ? discount.code : null);
      if (!checkout_url) {
        toast.error("Impossibile avviare il pagamento. Riprova.");
        setLoading(false);
        return;
      }
      // Esci dall'eventuale iframe (es. anteprima) così Stripe si apre a tutta pagina:
      // Stripe Checkout rifiuta di essere mostrato dentro un iframe.
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = checkout_url;
        } else {
          window.location.href = checkout_url;
        }
      } catch (_) {
        window.location.href = checkout_url;
      }
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(msg || "Errore durante il checkout. Riprova.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        className="bg-[#141A14] border-l border-white/10 text-[#E8E2D2] w-full sm:max-w-md flex flex-col p-0"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-6 border-b border-white/10">
          <SheetTitle className="font-serif-display text-3xl text-[#E8E2D2] text-left flex items-center gap-3">
            Il Carrello
            <span className="font-mono-accent text-sm text-[#D47738]">
              ({items.length})
            </span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-[#E8E2D2]/40">
            <ShoppingBag className="w-12 h-12" strokeWidth={1} />
            <p className="font-mono-accent text-xs uppercase tracking-[0.2em]">
              Il carrello è vuoto
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {items.map((i) => (
              <div
                key={i.product.id}
                className="flex gap-4 border-b border-white/10 pb-4"
                data-testid={`cart-item-${i.product.id}`}
              >
                <img
                  src={i.product.image}
                  alt={i.product.name}
                  className="w-20 h-24 object-cover bg-[#0D110D]"
                />
                <div className="flex-1 flex flex-col">
                  <div className="flex justify-between items-start gap-2">
                    <h4 className="font-serif-display text-xl leading-none">
                      {i.product.name}
                    </h4>
                    <button
                      onClick={() => removeItem(i.product.id)}
                      className="text-[#E8E2D2]/40 hover:text-[#9E3C3C] transition-colors"
                      data-testid={`remove-item-${i.product.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <span className="font-mono-accent text-[10px] uppercase tracking-widest text-[#E8E2D2]/40 mt-1">
                    {i.product.style}
                  </span>
                  <div className="mt-auto flex items-center justify-between">
                    <div className="flex items-center border border-white/20">
                      <button
                        onClick={() => updateQty(i.product.id, -1)}
                        className="p-2 hover:text-[#D47738] transition-colors"
                        data-testid={`decrease-${i.product.id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="font-mono-accent text-sm w-8 text-center">
                        {i.quantity}
                      </span>
                      <button
                        onClick={() => updateQty(i.product.id, 1)}
                        className="p-2 hover:text-[#D47738] transition-colors"
                        data-testid={`increase-${i.product.id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <span className="font-mono-accent text-[#D47738]">
                      € {(i.product.price * i.quantity).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="border-t border-white/10 px-6 py-6 space-y-4">
            <div className="space-y-2" data-testid="cart-totals">
              <div className="flex justify-between items-center">
                <span className="font-mono-accent text-[11px] uppercase tracking-[0.15em] text-[#E8E2D2]/50">
                  Subtotale
                </span>
                <span className="font-mono-accent text-sm text-[#E8E2D2]/80" data-testid="cart-subtotal">
                  € {total.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-mono-accent text-[11px] uppercase tracking-[0.15em] text-[#E8E2D2]/50">
                  Spedizione
                </span>
                <span className="font-mono-accent text-sm text-[#D47738]" data-testid="cart-shipping">
                  {shippingCost === 0 ? "Gratuita" : `€ ${shippingCost.toFixed(2)}`}
                </span>
              </div>
              {missingForFree > 0 && (
                <p className="font-mono-accent text-[10px] text-[#E8E2D2]/40 tracking-wider" data-testid="cart-free-hint">
                  Aggiungi € {missingForFree.toFixed(2)} per la spedizione gratuita
                </p>
              )}
              {discountAmount > 0 && (
                <div className="flex justify-between items-center">
                  <span className="font-mono-accent text-[11px] uppercase tracking-[0.15em] text-[#4A6E4A] flex items-center gap-1">
                    <Tag className="w-3 h-3" /> Sconto {discount.code}
                  </span>
                  <span className="font-mono-accent text-sm text-[#4A6E4A]" data-testid="cart-discount">
                    − € {discountAmount.toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            <div className="border border-white/10" data-testid="discount-section">
              {discount ? (
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="font-mono-accent text-[11px] uppercase tracking-[0.15em] text-[#4A6E4A] flex items-center gap-2">
                    <Tag className="w-3.5 h-3.5" /> {discount.code}
                  </span>
                  <button onClick={removeDiscount} className="text-[#E8E2D2]/50 hover:text-[#9E3C3C] transition-colors" data-testid="remove-discount-btn">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-2">
                  <input
                    placeholder="Codice sconto"
                    value={discountInput}
                    onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && applyDiscount()}
                    className="flex-1 bg-transparent border border-white/15 px-3 py-2.5 text-sm text-[#E8E2D2] uppercase focus:border-[#D47738] focus:outline-none"
                    data-testid="discount-input"
                  />
                  <button
                    onClick={applyDiscount}
                    disabled={applyingCode || !discountInput.trim()}
                    className="border border-white/20 px-4 py-2.5 font-mono-accent text-[11px] uppercase tracking-widest text-[#E8E2D2] hover:border-[#D47738] hover:text-[#D47738] transition-colors disabled:opacity-40"
                    data-testid="apply-discount-btn"
                  >
                    {applyingCode ? "..." : "Applica"}
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center border-t border-white/10 pt-4">
              <span className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#E8E2D2]/60">
                Totale
              </span>
              <span
                className="font-serif-display text-3xl text-[#E8E2D2]"
                data-testid="cart-total"
              >
                € {grandTotal.toFixed(2)}
              </span>
            </div>
            <p className="font-mono-accent text-[10px] text-[#E8E2D2]/30 uppercase tracking-wider">
              Eventuali imposte calcolate al checkout
            </p>

            <div className="border border-white/10 divide-y divide-white/10" data-testid="invoice-section">
              <label className="flex items-center gap-3 px-4 py-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={invoice.wants_invoice}
                  onChange={(e) => setInvoice({ ...invoice, wants_invoice: e.target.checked })}
                  className="accent-[#D47738] w-4 h-4"
                  data-testid="invoice-toggle"
                />
                <span className="font-mono-accent text-[11px] uppercase tracking-[0.15em] text-[#E8E2D2]/80">
                  Desidero la fattura (azienda)
                </span>
              </label>
              {invoice.wants_invoice && (
                <div className="p-4 space-y-2">
                  <input placeholder="Ragione sociale" value={invoice.company_name}
                    onChange={(e) => setInvoice({ ...invoice, company_name: e.target.value })}
                    className="w-full bg-transparent border border-white/20 px-3 py-2.5 text-sm text-[#E8E2D2] focus:border-[#D47738] focus:outline-none" data-testid="invoice-company" />
                  <input placeholder="Partita IVA" value={invoice.vat_number}
                    onChange={(e) => setInvoice({ ...invoice, vat_number: e.target.value })}
                    className="w-full bg-transparent border border-white/20 px-3 py-2.5 text-sm text-[#E8E2D2] focus:border-[#D47738] focus:outline-none" data-testid="invoice-vat" />
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Codice SDI" value={invoice.sdi_code}
                      onChange={(e) => setInvoice({ ...invoice, sdi_code: e.target.value })}
                      className="w-full bg-transparent border border-white/20 px-3 py-2.5 text-sm text-[#E8E2D2] focus:border-[#D47738] focus:outline-none" data-testid="invoice-sdi" />
                    <input placeholder="PEC" value={invoice.pec}
                      onChange={(e) => setInvoice({ ...invoice, pec: e.target.value })}
                      className="w-full bg-transparent border border-white/20 px-3 py-2.5 text-sm text-[#E8E2D2] focus:border-[#D47738] focus:outline-none" data-testid="invoice-pec" />
                  </div>
                  <p className="font-mono-accent text-[9px] text-[#E8E2D2]/30 uppercase tracking-wider">
                    Indica il Codice SDI oppure la PEC per la fattura elettronica
                  </p>
                </div>
              )}
            </div>

            <div className={`flex items-start gap-2 border px-4 py-3 ${belowMin ? "border-[#D47738]/50 bg-[#D47738]/10" : "border-[#4A6E4A]/40 bg-[#4A6E4A]/10"}`} data-testid="min-order-note">
              <Info className={`w-4 h-4 mt-0.5 shrink-0 ${belowMin ? "text-[#D47738]" : "text-[#4A6E4A]"}`} />
              <p className="font-mono-accent text-[10px] uppercase tracking-wider leading-relaxed text-[#E8E2D2]/70">
                Confezione minima {minQty} lattine. Nel carrello: <b className="text-[#E8E2D2]">{cans}</b>.
                {belowMin && <span className="text-[#D47738]"> Aggiungine ancora {missingCans}.</span>}
              </p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-consent-label">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="accent-[#D47738] w-4 h-4 mt-0.5 shrink-0"
                data-testid="terms-consent-checkbox"
              />
              <span className="font-mono-accent text-[10px] uppercase tracking-wider leading-relaxed text-[#E8E2D2]/70">
                Accetto le{" "}
                <Link to="/condizioni-acquisto" target="_blank" className="text-[#D47738] underline hover:text-[#E8E2D2]" data-testid="terms-link-acquisto">condizioni di acquisto</Link>,
                le{" "}
                <Link to="/condizioni-consegna" target="_blank" className="text-[#D47738] underline hover:text-[#E8E2D2]" data-testid="terms-link-consegna">condizioni di consegna</Link>{" "}
                e la{" "}
                <Link to="/politica-rimborso" target="_blank" className="text-[#D47738] underline hover:text-[#E8E2D2]" data-testid="terms-link-rimborso">politica di rimborso</Link>.
              </span>
            </label>

            {!cartEnabled && (
              <div className="flex items-start gap-2 border border-[#9E3C3C]/50 bg-[#9E3C3C]/15 px-4 py-3" data-testid="cart-closed-note">
                <Lock className="w-4 h-4 mt-0.5 shrink-0 text-[#9E3C3C]" />
                <p className="font-mono-accent text-[10px] uppercase tracking-wider leading-relaxed text-[#E8E2D2]/80">
                  Vendite momentaneamente sospese. Non è possibile completare l'ordine in questo momento.
                </p>
              </div>
            )}

            <button
              onClick={handleCheckout}
              disabled={loading || !cartEnabled || belowMin || !acceptedTerms}
              className="w-full bg-[#D47738] text-[#0D110D] py-4 font-mono-accent text-xs uppercase tracking-[0.2em] hover:bg-[#E8E2D2] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="checkout-btn"
            >
              {loading ? "Reindirizzamento..." : !cartEnabled ? "Vendite sospese" : belowMin ? `Minimo ${minQty} lattine` : "Procedi al Pagamento"}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
